import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { irisEnabled, IrisError, irisRequest, IrisSession, loadSession } from './iris';

type Props = { children: React.ReactNode; onSession?: (value: IrisSession) => void };
function CredentialForm({ action }: { action: 'activate' | 'complete' }) {
  const { t } = useTranslation('auth');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState(
    () => (window as unknown as { irisActivationCode?: string }).irisActivationCode || ''
  );
  useEffect(() => {
    delete (window as unknown as { irisActivationCode?: string }).irisActivationCode;
  }, []);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(false);
    try {
      if (!code) throw new IrisError(400);
      await irisRequest(
        `/auth/credentials/${action === 'complete' ? 'recovery/complete' : action}`,
        'POST',
        { code, new_password: password },
        false
      );
      setCode('');
      setPassword('');
      setDone(true);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack component="form" spacing={2} onSubmit={submit}>
      <Typography component="h2" variant="h5">
        {action === 'activate' ? t('activation') : t('recovery')}
      </Typography>
      {!code && !done && (
        <Alert severity="error" role="alert">
          {t('invalidLink')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" role="alert">
          {t('error')}
        </Alert>
      )}
      {done ? (
        <Alert severity="success" role="status">
          {t('done')}
        </Alert>
      ) : (
        <>
          <TextField
            label={t('password')}
            required
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            inputProps={{ 'data-testid': 'iris-password', 'data-test-id': 'iris-password', minLength: 12 }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !code}
            data-testid="iris-credential-submit"
            data-test-id="iris-credential-submit"
          >
            {busy ? <CircularProgress size={20} /> : t('submit')}
          </Button>
        </>
      )}
      <Button href="/login" data-testid="iris-back-login" data-test-id="iris-back-login">
        {t('signIn')}
      </Button>
    </Stack>
  );
}

export function IrisGate({ children, onSession }: Props) {
  const [state, setState] = useState<'loading' | 'ready' | 'signed-out' | 'error'>('loading');
  const [status, setStatus] = useState(0);
  const { t } = useTranslation('auth');
  const { pathname } = window.location;
  let action: 'recovery' | 'activate' | 'complete' | null = null;
  if (pathname.startsWith('/forgot-password')) action = 'recovery';
  if (pathname.startsWith('/register')) action = 'activate';
  if (pathname.startsWith('/set-password')) action = 'complete';
  const startPath = '/auth/start';
  useEffect(() => {
    if (!action && state === 'signed-out') window.location.replace(startPath);
  }, [action, state]);
  useEffect(() => {
    if (irisEnabled && action === 'recovery') window.location.replace('/auth/pages/forgot-password');
  }, [action]);
  const sessionCallback = useRef(onSession);
  sessionCallback.current = onSession;
  const check = useCallback(() => {
    setState('loading');
    loadSession(true)
      .then(value => {
        sessionCallback.current?.(value);
        setState('ready');
      })
      .catch((error: IrisError) => {
        if (error.status === 409) return;
        setStatus(error.status);
        setState(error.status === 401 ? 'signed-out' : 'error');
      });
  }, []);
  useEffect(() => {
    if (!irisEnabled || action) return undefined;
    const updated = (event: Event) => {
      sessionCallback.current?.((event as CustomEvent<IrisSession>).detail);
      setState('ready');
    };
    const changed = () => {
      setState('loading');
      window.location.reload();
    };
    window.addEventListener('iris:session-updated', updated);
    window.addEventListener('iris:identity-changed', changed);
    check();
    const ended = () => setState('signed-out');
    const logoutError = () => {
      setStatus(503);
      setState('error');
    };
    window.addEventListener('iris:logout-error', logoutError);
    window.addEventListener('iris:session-ended', ended);
    return () => {
      window.removeEventListener('iris:session-updated', updated);
      window.removeEventListener('iris:identity-changed', changed);
      window.removeEventListener('iris:session-ended', ended);
      window.removeEventListener('iris:logout-error', logoutError);
    };
  }, [action, check]);
  if (!irisEnabled) return <>{children}</>;
  if (!action && state === 'ready' && pathname !== '/login') return <>{children}</>;
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#4c4d57', display: 'grid', placeItems: 'center', p: 3 }}>
      <Stack spacing={3} sx={{ width: '100%', maxWidth: 420, bgcolor: 'background.paper', p: 4, borderRadius: 2 }}>
        <Typography component="h1" variant="h4" sx={{ color: '#008f88', fontWeight: 700 }}>
          IRIS Cloud
        </Typography>
        {action && action !== 'recovery' && <CredentialForm action={action} />}
        {action === 'recovery' && (
          <Box role="status">
            <CircularProgress size={24} /> {t('loading')}
          </Box>
        )}
        {!action && state === 'loading' && (
          <Box role="status">
            <CircularProgress size={24} /> {t('loading')}
          </Box>
        )}
        {!action && state !== 'loading' && (
          <>
            <Typography variant="h5">{t('signIn')}</Typography>
            {state === 'error' && (
              <Alert severity="error" role="alert">
                {status === 403 ? t('denied') : t('error')}
              </Alert>
            )}
            {state === 'error' ? (
              <Button data-testid="iris-retry" data-test-id="iris-retry" onClick={check}>
                {t('retry')}
              </Button>
            ) : (
              <Button
                variant="contained"
                data-testid="iris-sign-in"
                data-test-id="iris-sign-in"
                href={state === 'ready' ? '/' : startPath}
              >
                {t('continue')}
              </Button>
            )}
            <Button data-testid="iris-recovery" data-test-id="iris-recovery" href="/auth/pages/forgot-password">
              {t('forgot')}
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
